import type {
    MutableToolStep,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import { hasAllRequiredBindingsForToolStep } from '@/workflow-builder/_pure/model/machineWorkflow';
import { deriveNextUnboundInputRoleName } from '@/workflow-builder/_pure/model/toolStepDraft';
import type { Phase } from '@/workflow-builder/_contract/runtime';

export function deriveNextBindingPhaseFromSelection(args: {
    currentPhase: Phase;
    selectedToolStep: MutableToolStep | null;
    draftWorkflowState: MutableWorkflowState;
}): 'binding_inputs' | 'reviewing_input_bindings' {
    const allInputsBound = args.selectedToolStep
        ? hasAllRequiredBindingsForToolStep({
            draftWorkflowState: args.draftWorkflowState,
            toolStep: args.selectedToolStep,
            includeDynamicRoles: false,
        })
        : false;

    if (args.currentPhase === 'reviewing_input_bindings') {
        return 'reviewing_input_bindings';
    }

    return allInputsBound ? 'reviewing_input_bindings' : 'binding_inputs';
}

export function deriveBindingProgressionAfterBind(args: {
    currentPhase: Phase;
    selectedToolStep: MutableToolStep | null;
    selectedInputRoleName: string | null;
    draftWorkflowState: MutableWorkflowState;
    bindableInputRoleNames: readonly string[];
}): {
    nextPhase: 'binding_inputs' | 'reviewing_input_bindings';
    nextSelectedInputRoleName: string | null;
} {
    const nextPhase = deriveNextBindingPhaseFromSelection({
        currentPhase: args.currentPhase,
        selectedToolStep: args.selectedToolStep,
        draftWorkflowState: args.draftWorkflowState,
    });

    if (args.selectedToolStep === null) {
        return {
            nextPhase,
            nextSelectedInputRoleName: args.selectedInputRoleName,
        };
    }

    if (nextPhase === 'reviewing_input_bindings') {
        return {
            nextPhase,
            nextSelectedInputRoleName: args.selectedInputRoleName,
        };
    }

    const roleNames = args.bindableInputRoleNames;
    const roleEntries = args.draftWorkflowState[args.selectedToolStep.stepKey] ?? {};
    const inputBindings: Record<string, true | null> = Object.fromEntries(
        roleNames.map((roleName) => [roleName, roleName in roleEntries ? true : null]),
    );

    return {
        nextPhase,
        nextSelectedInputRoleName: deriveNextUnboundInputRoleName({
            roleNames,
            inputBindings,
            lastBoundRoleName: args.selectedInputRoleName ?? undefined,
        }),
    };
}
